import * as React from "react";
import { useState, useEffect } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";
import { useToast } from "@/components/ui/use-toast";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PencilLine, ArrowLeft } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { getResult, lastgetResult } from "@/services/finance";

import type {
  FinancialDetail,
  FinancialResponse,
} from "@/types/finance";

import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

import { ModalRequireEditDoc } from "@/components/ModalRequireEditDoc";

// Service and Utils
import { formatNumber } from "@/utils/formatNumber";
import { RequireEditDocs } from "@/services/require-edit-docs";
import calculateDaysFromNow from "@/utils/calculateDaysFromNow";

type FormattedFinancialDetail = {
  finDetailType: FinancialDetail["finDetailType"];
  financialType: FinancialDetail["financialType"];
  name: string;
  amount: string;
  amountForBackend: number;
  remark: string;
};

const finDetailTypeNames: Record<string, string> = {
  INITIAL: "ຜົນໄດ້ຮັບ ເບື້ອງ​ຕົ້ນ",
  OPERATING: "ຜົນໄດ້ຮັບ ໃນທຸລະກິດ",
  PRE_TAX: "ຜົນໄດ້ຮັບ ກ່ອນ​ການ​ເສຍ​ອາ​ກອນ",
  NET_OPERATING: "ຜົນໄດ້ຮັບ ສຸດ​ທິຈາກ​ການ​ດຳ​ເນີນ​ງານ",
  AFTER_TAX: "ຜົນໄດ້ຮັບສັງລວມອື່ນໆ ຫຼັງອາກອນ",
  ANNUAL_NET: "ຜົນໄດ້ຮັບສຸດ​ທິ​ໃນ​ປີ",
};

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/result/"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      refId: search.refId as string | undefined,
    };
  },
});

function RouteComponent() {
  const { refId } = Route.useSearch();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [unit, setUnit] = React.useState("1");
  const unitValue = Number(unit);
  const companyId = localStorage.getItem("companyId");
  const year = Number(localStorage.getItem("year"));
  const { company_financial_report } = useAccessPermission();
  const [thisYearData, setThisYearData] = useState<FormattedFinancialDetail[]>([]);
  const [lastYearData, setLastYearData] = useState<FormattedFinancialDetail[]>([]);
  const [open, setOpen] = React.useState(false);


  React.useEffect(() => {
    if (!year) {
      navigate({
        to: "/report/finance/company/result/add",
        replace: true,
      });
    }
  }, [year, navigate]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["getResult", companyId, year],
    queryFn: () => getResult({ companyId: companyId as string, yearly: year }),
    enabled: Boolean(companyId && year),
  });

  const {
    data: lastdata,
    isLoading: lastisLoading,
  } = useQuery({
    queryKey: ["lastGetResult", companyId, year],
    queryFn: () =>
      lastgetResult({ companyId: companyId as string, yearly: year - 1 }),
    enabled: Boolean(companyId && year),
  });
  const resultData: FinancialResponse = data?.data;
  const lastresultData: FinancialResponse = lastdata?.data;

  const dayFromNow = calculateDaysFromNow(resultData?.updatedAt);

  const requireEditMutation = useMutation({
    mutationFn: (data: {
      editId: string;
      editRequestType: string;
      pendingReason: string;
      documentFile: string;
      editRequestStatus: string;
    }) => RequireEditDocs(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getResult", companyId, year],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ທ່ານໄດ້ສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານແລ້ວ",
        duration: 5000,
        variant: "success",
      });
      setOpen(false);
    },
    onError: () => {
      toast({
        title: "ຜິດພາດ",
        description:
          "ການສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານ ບໍ່ສາມາດເຮັດໄດ້ ກະລຸນາລອງໃໝ່ ຫຼື ຕິດຕໍ່ ຜູ້ໃຊ້ ຖ້າ ວ່າ ຍັງ ມີ ບັນ ຫາ",
        duration: 5000,
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (data?.data) {
      const initialFinancialDetails = data.data.financialDetails.map(
        (detail: FinancialDetail) => {
          // Determine if the amount is negative
          const isNegative = detail.financialType === "EXPENSE";

          // Format the amount string based on whether it's negative
          const formattedAmount = isNegative
            ? `(${Math.abs(detail.amount)})`
            : String(detail.amount);

          return {
            finDetailType: detail.finDetailType,
            financialType: detail.financialType,
            name: detail.name,
            amount: formattedAmount,
            amountForBackend: detail.amount, // Keep the original numeric value
            remark: detail.remark,
          };
        }
      );
      setThisYearData(initialFinancialDetails);
    }

    if (lastdata?.data) {
      const initialFinancialDetails = lastdata.data.financialDetails.map(
        (detail: FinancialDetail) => {
          // Determine if the amount is negative
          const isNegative = detail.financialType === "EXPENSE";

          // Format the amount string based on whether it's negative
          const formattedAmount = isNegative
            ? `(${Math.abs(detail.amount)})`
            : String(detail.amount);

          return {
            finDetailType: detail.finDetailType,
            financialType: detail.financialType,
            name: detail.name,
            amount: formattedAmount,
            amountForBackend: detail.amount, // Keep the original numeric value
            remark: detail.remark,
          };
        }
      );
      setLastYearData(initialFinancialDetails);
    }
  }, [data, lastdata]);

  const finDetailTypeOrder = [
    "INITIAL",
    "OPERATING",
    "PRE_TAX",
    "NET_OPERATING",
    "AFTER_TAX",
    "ANNUAL_NET",
  ];

  const groupAndCalculateFinancials = (details: any[] = []) => {
    // Group by finDetailType
    const grouped = details.reduce(
      (acc, detail) => {
        if (!acc[detail.finDetailType]) {
          acc[detail.finDetailType] = [];
        }
        acc[detail.finDetailType].push(detail);
        return acc;
      },
      {} as Record<string, any[]>
    );

    // FIXED: Calculate individual sums for each group
    const calculateGroupValue = (items: any[]) => {
      return items.reduce((sum, item) => {
        // Get the actual numeric value
        let value = 0;

        if (
          item.amountForBackend !== undefined &&
          item.amountForBackend !== null
        ) {
          value = Number(item.amountForBackend);
        } else if (item.amount !== undefined && item.amount !== null) {
          // Parse the amount string, removing parentheses and commas
          const cleanAmount = String(item.amount).replace(/[(),]/g, "");
          value = Number(cleanAmount);

          // If original amount was in parentheses, make it negative
          if (String(item.amount).includes("(")) {
            value = -Math.abs(value);
          }
        }

        // IMPORTANT: If the financialType is EXPENSE but the value is positive,
        // make it negative (this handles cases where expenses don't have parentheses)
        if (item.financialType === "EXPENSE" && value > 0) {
          value = -Math.abs(value);
        }

        // Simply add the value - expenses are now guaranteed to be negative
        return sum + value;
      }, 0);
    };

    // Calculate cumulative totals based on type
    const groupSums = (Object.entries(grouped) as [string, any[]][]).map(([type, items]) => {
      let netTotal = 0;

      switch (type) {
        case "INITIAL":
          netTotal = calculateGroupValue(items);
          break;

        case "OPERATING":
          netTotal =
            calculateGroupValue(grouped["INITIAL"] || []) +
            calculateGroupValue(items);
          break;

        case "PRE_TAX":
          netTotal =
            calculateGroupValue(grouped["INITIAL"] || []) +
            calculateGroupValue(grouped["OPERATING"] || []) +
            calculateGroupValue(items);
          break;

        case "NET_OPERATING":
          netTotal =
            calculateGroupValue(grouped["INITIAL"] || []) +
            calculateGroupValue(grouped["OPERATING"] || []) +
            calculateGroupValue(grouped["PRE_TAX"] || []) +
            calculateGroupValue(items);
          break;

        case "AFTER_TAX":
          netTotal = calculateGroupValue(items);
          break;

        case "ANNUAL_NET":
          netTotal =
            calculateGroupValue(grouped["NET_OPERATING"] || []) +
            calculateGroupValue(grouped["AFTER_TAX"] || []);
          break;

        default:
          netTotal = calculateGroupValue(items);
      }

      // Calculate separate income and expense for reference (optional)
      const income = items
        .filter((item: any) => item.financialType === "INCOME")
        .reduce((sum: number, item: any) => {
          const value =
            item.amountForBackend !== undefined
              ? Number(item.amountForBackend)
              : Number(String(item.amount).replace(/[(),]/g, ""));
          return sum + Math.abs(value); // Income should be positive
        }, 0);

      const expense = items
        .filter((item: any) => item.financialType === "EXPENSE")
        .reduce((sum: number, item: any) => {
          const value =
            item.amountForBackend !== undefined
              ? Number(item.amountForBackend)
              : Number(String(item.amount).replace(/[(),]/g, ""));
          return sum + Math.abs(value); // Expense should be positive for display
        }, 0);

      return {
        type,
        items,
        income,
        expense,
        netTotal,
      };
    });

    // Sort groupSums according to finDetailTypeOrder
    const sortedGroupSums = groupSums.sort((a, b) => {
      const indexA = finDetailTypeOrder.indexOf(a.type);
      const indexB = finDetailTypeOrder.indexOf(b.type);
      return indexA - indexB;
    });

    return sortedGroupSums;
  };

  React.useEffect(() => {
    if (!data?.data && !isLoading) {
      navigate({
        to: "/report/finance/company/result/add",
        replace: true,
      });
    }
  }, [data, navigate, isLoading]);

  const groupedFinancials = React.useMemo(() => {
    return groupAndCalculateFinancials(resultData?.financialDetails);
  }, [resultData?.financialDetails]);

  const lastgroupedFinancials = React.useMemo(() => {
    return groupAndCalculateFinancials(lastresultData?.financialDetails); // Same function!
  }, [lastresultData?.financialDetails]);

  const formatNumberWithCommas = (
    value: number | string,
    type: "INCOME" | "EXPENSE"
  ) => {
    // Convert to string and remove commas
    const stringValue = String(value || "").replace(/,/g, "");

    // Convert to number for processing
    let numericValue = Number(stringValue.replace(/[()]/g, ""));

    // Handle negative numbers
    if (stringValue.startsWith("(") || stringValue.startsWith("-")) {
      numericValue = Math.abs(numericValue);
    }

    // If the value is invalid (NaN), return the original value
    if (Number.isNaN(numericValue)) {
      return value;
    }

    // Format number with commas and preserve decimal places
    const formattedValue = numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // Return with parentheses for EXPENSE type
    return type === "EXPENSE" ? `(${formattedValue})` : formattedValue;
  };

  const calculateTotal = (
    type: string,
    dataSet: FormattedFinancialDetail[]
  ): number => {

    return dataSet
      ?.filter((detail) => detail?.finDetailType === type)
      ?.reduce((sum: number, detail: FormattedFinancialDetail) => {
        // Get the numeric value
        let value = 0;

        if (
          detail?.amountForBackend !== undefined &&
          detail?.amountForBackend !== null
        ) {
          value = Number(detail.amountForBackend);
        } else if (detail?.amount !== undefined && detail?.amount !== null) {
          // Parse the amount string, removing parentheses and commas
          const cleanAmount = String(detail.amount).replace(/[(),]/g, "");
          value = Number(cleanAmount);

          // If original amount was in parentheses, make it negative
          if (String(detail.amount).includes("(")) {
            value = -Math.abs(value);
          }
        }

        // IMPORTANT: If the financialType is EXPENSE but the value is positive,
        // make it negative (this handles cases where expenses don't have parentheses)
        if (detail?.financialType === "EXPENSE" && value > 0) {
          value = -Math.abs(value);
        }

        // Simply add the value - expenses are now guaranteed to be negative
        return sum + value;
      }, 0);
  };

  // Total This Year
  const totalThisYearInitial = React.useMemo(
    () => calculateTotal("INITIAL", thisYearData),
    [thisYearData]
  );

  const totalThisYearOperating = React.useMemo(
    () => calculateTotal("OPERATING", thisYearData),
    [thisYearData]
  );

  const totalThisYearPreTax = React.useMemo(
    () => calculateTotal("PRE_TAX", thisYearData),
    [thisYearData]
  );

  const totalThisYearNetOperating = React.useMemo(
    () => calculateTotal("NET_OPERATING", thisYearData),
    [thisYearData]
  );

  const totalThisYearAfterTax = React.useMemo(
    () => calculateTotal("AFTER_TAX", thisYearData),
    [thisYearData]
  );

  // Total Last Year
  const totalLastYearInitial = React.useMemo(
    () => calculateTotal("INITIAL", lastYearData),
    [lastYearData]
  );
  const totalLastYearOperating = React.useMemo(
    () => calculateTotal("OPERATING", lastYearData),
    [lastYearData]
  );
  const totalLastYearPreTax = React.useMemo(
    () => calculateTotal("PRE_TAX", lastYearData),
    [lastYearData]
  );
  const totalLastYearNetOperating = React.useMemo(
    () => calculateTotal("NET_OPERATING", lastYearData),
    [lastYearData]
  );
  const totalLastYearAfterTax = React.useMemo(
    () => calculateTotal("AFTER_TAX", lastYearData),
    [lastYearData]
  );

  if (!companyId || !year) {
    return <div>Missing required data</div>;
  }

  if (error) return <div>Error loading data</div>;

  const handleRequireEdit = ({
    reason,
    file,
  }: {
    reason: string;
    file: string;
  }) => {
    if (!data?.data?.id) return;
    requireEditMutation.mutate({
      editId: data?.data?.id,
      editRequestType: "FINANCIAL_REPORT",
      pendingReason: reason,
      documentFile: file,
      editRequestStatus: "REQUEST",
    });
  };

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: refId
                  ? `/report/action-plan/${refId}yearly=${year}`
                  : "/report/finance",
              })
            }
            className="flex items-center gap-[4px]  mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>

          <CardTitle>ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {year}</CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>

          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {dayFromNow <= 3 ? company_financial_report.isUpdate && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/result/edit",
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md  p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            ) :
              company_financial_report.isUpdate && (
                resultData?.editRequestStatus === "REQUEST" ? (
                  <p className="text-red-500">ກະລຸນາລໍຖ້າການກວດສອບ</p>
                ) : (
                  <Button
                    onClick={() => setOpen(true)}
                    variant="secondary"
                    className="flex items-center border border-blue-500 gap-x-1 w-[120px] p-2 text-blue-500"
                  >
                    <PencilLine /> ຂໍແກ້ໄຂເອກະສານ
                  </Button>
                ))}
          </div>
          <ModalRequireEditDoc
            isOpen={open}
            onClose={() => setOpen(false)}
            onConfirm={handleRequireEdit}
          />
        </CardHeader>
        <CardContent>

          <div className="mb-4">
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          <div>
            {isLoading || lastisLoading ? (
              <DinamixSkeletonTable />
            ) : (
              <Table className="mb-4">
                <TableHeader>
                  <TableRow className="[&>*]:font-bold [&>*]:text-black [&>*]:whitespace-nowrap">
                    <TableHead className="border">ເນຶ້ອໃນລາຍງານ</TableHead>
                    <TableHead className="text-left border">
                      ມູນຄ່າຍັງເຫຼືອປີນີ້ {year}
                    </TableHead>
                    <TableHead className="text-left border">
                      ມູນຄ່າຍັງເຫຼືອປີທີ່ຜ່ານມາ {year - 1}
                    </TableHead>
                    <TableHead className="border">ໝາຍເຫດ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedFinancials.map((group) => (
                    <React.Fragment key={group.type}>
                      {group.items.map((detail) => (
                        <TableRow key={detail.id} className="cursor-pointer">
                          <TableCell className="border whitespace-nowrap">
                            {detail.name}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumberWithCommas(
                              detail.amount / unitValue,
                              detail.financialType
                            )}
                          </TableCell>

                          {/* last year */}
                          <TableCell className="text-right border">
                            {(() => {
                              const lastDetail = lastgroupedFinancials
                                ?.find((g) => g.type === group.type)
                                ?.items?.find(
                                  (item) => item.name === detail.name
                                );

                              return formatNumberWithCommas(
                                (lastDetail?.amount || 0) / unitValue,
                                lastDetail?.financialType || "EXPENSE"
                              );
                            })()}
                          </TableCell>

                          <TableCell className="border">
                            {detail.remark}
                          </TableCell>
                        </TableRow>
                      ))}

                      <TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                        <TableCell className="border">
                          {finDetailTypeNames[group.type] || group.type}
                        </TableCell>
                        <TableCell className="text-right border">
                          {formatNumber(group.netTotal / unitValue)}
                          {/* {formatNumberWithCommas(
														group.netTotal / unit,
														group.type,
													)} */}
                        </TableCell>
                        {/* last year */}
                        <TableCell className="text-right border">
                          {lastgroupedFinancials?.find(
                            (g) => g.type === group.type
                          )?.netTotal
                            ? (
                              lastgroupedFinancials.find(
                                (g) => g.type === group.type
                              )!.netTotal / unitValue
                            ).toLocaleString()
                            : "0"}
                        </TableCell>
                        <TableCell className="text-left border"> </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                  <TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                    <TableCell className="border whitespace-nowrap">
                      ຜົນໄດ້ຮັບສຸດທິໃນປີ
                    </TableCell>
                    <TableCell className="text-right border">
                      {/* Sum of current year */}
                      {/* {formatNumber(
												(groupedFinancials.find(g => g.type === "NET_OPERATING")?.netTotal || 0 +
													groupedFinancials.find(g => g.type === "AFTER_TAX")?.netTotal || 0) / unit
											)} */}
                      {formatNumber(
                        totalThisYearOperating +
                        totalThisYearInitial +
                        totalThisYearPreTax +
                        totalThisYearNetOperating +
                        totalThisYearAfterTax
                      )}
                    </TableCell>

                    <TableCell className="text-right border">
                      {formatNumber(
                        totalLastYearOperating +
                        totalLastYearInitial +
                        totalLastYearPreTax +
                        totalLastYearNetOperating +
                        totalLastYearAfterTax
                      )}
                    </TableCell>
                    <TableCell className="text-left border">- </TableCell>
                  </TableRow>
                  <TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                    <TableCell className="border whitespace-nowrap">
                      ທີ່ເປັນສ່ວນຂອງ
                    </TableCell>
                    <TableCell className="text-left border">-</TableCell>
                    {/* last year */}
                    <TableCell className="text-left border">-</TableCell>
                    <TableCell className="text-left border">- </TableCell>
                  </TableRow>
                  <TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                    <TableCell className="border whitespace-nowrap">
                      ພູດສ່ວນ ຂອງຜົນປະໂຫຍດສ່ວນນ້ອຍ(1)
                    </TableCell>
                    <TableCell className="text-left border">-</TableCell>
                    {/* last year */}
                    <TableCell className="text-left border">-</TableCell>
                    <TableCell className="text-left border">- </TableCell>
                  </TableRow>
                  <TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                    <TableCell className="border whitespace-nowrap">
                      ພູດສ່ວນ ຂອງກຸ່ມ(1)
                    </TableCell>
                    <TableCell className="text-left border">-</TableCell>
                    {/* last year */}
                    <TableCell className="text-left border">-</TableCell>
                    <TableCell className="text-left border">- </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}

            {resultData?.docFile
              ? company_financial_report.isDownload && (
                <FileHandleViewer fileUrl={resultData?.docFile} />
              )
              : null}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
